import { uploadChunked, UPLOAD_MAX, uploadErrText, isCanceled } from "./upload";
import { authToken } from "./api";

/**
 * attach.js —— 附件（图片 / 视频 / PDF / 表格 / 文档 / 压缩包）上传与展示的共用工具。
 *
 * 需求来源（2026-10-15，用户）：需求反馈里要能提交图片、视频、PDF、表格等任意附件；
 * 图片和视频要压缩；大文件要切片上传；查看时在页面内预览，不跳外部标签页。
 *
 * 三层分工（2026-10-15 用户拍板「图片按用途分档 + 全局加压缩」后确定，别搞混）：
 *   ① **前端压缩（治「上传慢」）**：`lib/upload.js` 的 uploadChunked 统一挂了一道
 *      `shrinkImage`，全站切片上传自动生效。本模块只负责**选档位**：
 *      `SHOT_PRESET = "shot"` → 长边 1600 ≈ 300KB，反馈 / 聊天截图用（看 Bug 不需要原画质）。
 *      这才是解决「大图上传慢」的那一环 —— 后端压缩救不了上传耗时。
 *   ② **后端共享压缩（治存储与回放带宽）**：见 `backend/media_compress.py`，接在
 *      `/uploads/finalize` + `/files/{fid}/download` 上，全站生效、调用方零改动。
 *      视频由它做 ffmpeg 异步转码（720p/CRF28）；本模块的 `resolveAtt` 只读它写的状态
 *      来显示「压缩中 / 已压缩」。
 *   ③ **切片上传**：`lib/upload.js`（2MB 分片 / 断点续传 / 重试 / SHA-256 / 200MB）。
 *
 * 与后端 `backend/feedback.py` 的分工：
 *   · 类型判定 / 白名单 / 大小上限在两处各有一份（前端做即时反馈，后端做权威校验），
 *     **改一处必须同时改另一处**，否则会出现「前端放进来了、后端 400」的错位。
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// 单文件上限：与后端 MAX_FILE_BYTES、切片上传上限保持一致（200MB）
export const ATTACH_MAX = UPLOAD_MAX;
// 单个反馈 / 单条留言的附件数量上限（与后端 MAX_ATTACHMENTS 一致）
export const ATTACH_COUNT_MAX = 12;

/**
 * 需求反馈 / 聊天发图用的压缩档位（见 imageShrink 的 IMG_PRESETS.shot）。
 *
 * 2026-10-20 调整：交给 uploadChunked 的 preset 现在输出 **WebP**、长边 2880。
 * 之所以不再是「1600 够看 Bug 了」：1600 会把 2K/4K 截图的正文小字压到发虚，
 * 而实测 2880 长边 WebP 只有约 230KB（旧 1600 JPEG 是 210KB）—— 体积几乎不变，
 * 清晰度翻倍，聊天 / 反馈里的报错截图能看清日志行号。
 */
export const SHOT_PRESET = "shot";

/**
 * 文件选择框的 accept。列全扩展名而不是只写 `image/*,video/*`：
 * Windows 上部分浏览器对 .xlsx / .docx 不会按 MIME 归类，写扩展名更可靠。
 */
export const ATTACH_ACCEPT =
  "image/*,video/*,.pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.txt,.log,.json,.zip,.rar,.7z";

const IMG = /\.(png|jpe?g|gif|webp|bmp|heic|heif|avif|svg)$/i;
const VID = /\.(mp4|mov|m4v|webm|avi|3gp|mkv)$/i;
const SHEET = /\.(xls|xlsx|csv)$/i;
const PDF = /\.pdf$/i;

/**
 * 附件大类：image | video | pdf | sheet | file —— 与后端 `_kind_of` 同口径。
 * ⚠️ 判定顺序有意义：pdf 必须在 sheet / file 之前（pdf 不在 sheet 名单里，
 * 但显式前置可以避免以后有人把 pdf 加进名单时改变行为）。
 */
export const kindOf = (name = "", contentType = "") => {
  const ct = (contentType || "").toLowerCase();
  const n = name || "";
  if (PDF.test(n) || ct === "application/pdf") return "pdf";
  if (ct.startsWith("image/") || IMG.test(n)) return "image";
  if (ct.startsWith("video/") || VID.test(n)) return "video";
  if (SHEET.test(n) || ct === "text/csv" || ct.includes("spreadsheetml") || ct === "application/vnd.ms-excel") return "sheet";
  return "file";
};

/** 是否属于允许上传的类型（前端即时拦截，后端还会再校验一次） */
export const isAllowedFile = (f) => kindOf(f?.name, f?.type) !== "file" || /\.(txt|log|json|zip|rar|7z|docx?|pptx?)$/i.test(f?.name || "");

/** 附件读取直链（带鉴权 query），与 chat / HubDiscussion 的写法一致。
 *  ⚠️ 必须走 `authToken()`（先读存储、再回退到 axios 实例上实际在用的 token），
 *  不能直接读 localStorage：勾了「仅本次会话」的用户 token 只在 sessionStorage 里，
 *  而更隐蔽的一种情况是存储里压根没有、token 只活在 axios 实例上 —— 那时拼出来的是
 *  `?auth=`（空），图片会被 Chrome 的 ORB 拦掉变成破图（2026-10-15 实测踩到）。
 */
export const fileUrl = (fid) => `${BACKEND}/api/files/${fid}/download?auth=${encodeURIComponent(authToken())}`;

/** 人类可读体积 */
export const fmtBytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(0)} KB`;
  return `${(v / 1024 / 1024).toFixed(v < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

/**
 * 把附件记录解析成「前端可直接渲染」的形态。
 *
 * 视频 / 大图上传后会由**服务端共享压缩层**（backend/media_compress.py）异步压一遍，
 * `media`（来自 GET /feedback/{id} 的 `media` 覆盖层，按 fileId 索引）里记着状态：
 *   · done       → 界面上标「已压缩」，并显示压缩后体积
 *   · processing → 视频还在转，标「压缩中」
 *   · skipped / failed → 静默（压缩是优化项，不该打扰用户）
 *
 * ⚠️ URL 一律用**原 fileId**：`/files/{fid}/download` 在有压缩版时会自动回源压缩版，
 *    所以前端不需要、也不该自己换 fileId（换了反而绕过了权限与回源逻辑）。
 * 历史数据没有 aid / media → 直接回退到 fileId 本身。
 */
export const resolveAtt = (att, media) => {
  const m = (media || {})[att?.fileId] || null;
  const kind = att?.kind || kindOf(att?.name, att?.contentType);
  const fileId = att?.fileId;
  return {
    aid: att?.aid || att?.fileId,
    fileId,
    name: att?.name || "file",
    kind,
    status: m?.status || "ready",
    url: fileId ? fileUrl(fileId) : "",
    size: att?.size,
    // 压缩后体积（服务端报的），用于在 UI 上写「已压缩 12MB → 2MB」
    compressedSize: m?.status === "done" ? m.size : null,
    processing: m?.status === "processing",
    compressed: m?.status === "done",
  };
};

/**
 * 上传单个附件：图片按「截图档」压缩后统一切片上传。
 *
 * @param {object}   api     AppContext 里的 axios 实例
 * @param {File}     file    用户选的文件
 * @param {object}   opts    { signal, onProgress({pct,state,name}), preset }
 * @returns {Promise<{fileId,filename,contentType,size,kind}>}
 *
 * 压缩不在这里做 —— 交给 `uploadChunked`（共享上传层），本函数只负责**选档位**：
 * 需求反馈 / 聊天以截图为主，走 "shot" 档（长边 1600 ≈ 300KB），这是解决「大图上传慢」的关键。
 * 失败时直接把 uploadChunked 的错误抛出去（调用方用 uploadErrText 取中文文案）；
 * 取消（AbortController）抛的是 isCanceled 能识别的错误，调用方据此静默跳过。
 */
export async function uploadAttachment(api, file, { signal, onProgress, preset = SHOT_PRESET } = {}) {
  if (!file) throw new Error("no file");
  if (file.size > ATTACH_MAX) {
    const e = new Error(`${file.name} 超过 ${Math.round(ATTACH_MAX / 1024 / 1024)}MB 上限`);
    e.friendly = true;
    throw e;
  }
  const kind = kindOf(file.name, file.type);
  if (kind === "file" && !isAllowedFile(file)) {
    const e = new Error(`不支持的文件类型：${file.name}`);
    e.friendly = true;
    throw e;
  }
  onProgress?.({ pct: 0, state: "preparing", name: file.name });
  const r = await uploadChunked(api, file, { signal, shrink: preset, onProgress: (p) => onProgress?.({ ...p, name: file.name }) });
  const contentType = r.contentType || file.type || "";
  return {
    fileId: r.fileId,
    filename: r.filename || file.name,
    contentType,
    size: r.size ?? file.size,
    // 后端会按扩展名重新判 kind，这里只做前端即时展示用
    kind: (r.kind && r.kind !== "file" ? r.kind : kind) === "image" ? "image" : (r.kind || kind),
  };
}

export { uploadErrText, isCanceled };
